/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { allActionsSucceeded, apiCall } from 'app/shared/lib/essette-api';
import { IServiceSearchFilters, LookupService } from 'app/cd/services/lookup-service';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { getReference } from 'app/shared/models/essette-types';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';

@autoinject()
export class AddCommonServices extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    public serviceSearchQuery: string | null = null;

    protected serviceSearchResults: CD.Service[] = [];
    protected currentServices: UM.CommonService[] = [];
    protected selectedServices: CD.Service[] = [];

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCommonServices();
    }

    protected addService(service: CD.Service) {
        this.selectedServices = pushCopy(this.selectedServices, service);
        this.serviceSearchQuery = '';
        this.serviceSearchResults.splice(0);
    }

    protected removeSelectedService(service: CD.Service) {
        this.selectedServices = spliceCopy(this.selectedServices, this.selectedServices.indexOf(service, 0), 1);
        if(this.selectedServices.length === 0) this.$autocomplete.clear();
    }

    protected filterServices = (service: CD.Service): boolean => {
        if(this.selectedServices.some(s => service.id === s.id)) {
            return false;
        }
        if(!this.currentServices && !service) { return false; }
        return !this.currentServices.some(s => service.id === s.id);
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.saveSelectedServices();
    }

    public reset(): void {
        this.selectedServices.splice(0);
        this.serviceSearchQuery = '';
        this.serviceSearchResults.splice(0);
        this.$autocomplete.clear();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async searchServices(newValue: string): Promise<void> {
        if(!newValue) {
            if(this.serviceSearchResults) this.serviceSearchResults.splice(0);
            return;
        }

        const options: Partial<IQueryLoadOptions> = { top: 30 };

        try {
            this.state.beginLoad();

            const serviceSearchFilters: IServiceSearchFilters = { codeOrDescription: newValue };
            const response = await this.lookupService.searchServices(undefined, options, [], true, serviceSearchFilters); // filter by code or description
            this.serviceSearchResults = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCommonServices(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.authService.loadCommonServices({ all: true });
            this.currentServices = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async saveSelectedServices(): Promise<boolean> {
        let servicesToSave: UM.CommonService[] = this.selectedServices.map(s => { return { service: getReference(s) } });
        let isSuccessful = false;
        try {
            this.state.beginSave();

            const progressToast = this.toaster.standard.batch('um:commonService.$long', servicesToSave.length);
            progressToast.show();

            const responses = await this.authService.insertCommonServicesBatch(servicesToSave, progressToast);
            isSuccessful = allActionsSucceeded(responses);

            return isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:commonService.$long', isSuccessful, servicesToSave.length);
            this.state.endSave();
        }

    }
}
